import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { get as _get } from "lodash";
import { explore } from "../services/ExploreController";
import { CONFIG_BASE } from "../constants/configs";

// the explore pages refresh every minute while visible; the lists are shared by every explore page
const EXPLORE_QUERY = { staleTime: 30 * 1000, refetchInterval: 60 * 1000 };
const PERIPHERY = _get(CONFIG_BASE, "contracts.periphery.address", "");

export const useProtocolStats = () =>
  useQuery({ queryKey: ["explore", "protocol"], queryFn: () => explore.getProtocol(), ...EXPLORE_QUERY });

/**
 * The token list entry (canonical symbol, name and logo) for each API token; the API spells KOIN as "koin"
 */
export const useListTokens = () => {
  const base = useSelector((state) => state.tokens.base_tokens);
  const custom = useSelector((state) => state.tokens.custom_tokens);
  return useMemo(() => new Map(base.concat(custom).map((token) => [token.address, token])), [base, custom]);
};

const withListData = (token, listTokens) => {
  if (!token) return token;
  const listed = listTokens.get(token.id);
  return {
    ...token,
    address: token.id,
    symbol: _get(listed, "symbol", token.symbol),
    name: _get(listed, "name", token.name),
    logoURI: _get(listed, "logoURI", null),
    listed: Boolean(listed),
  };
};

const withPoolTokens = (pool, listTokens) =>
  pool && { ...pool, token0: withListData(pool.token0, listTokens), token1: withListData(pool.token1, listTokens) };

export const useExploreTokens = () => {
  const listTokens = useListTokens();
  const query = useQuery({ queryKey: ["explore", "tokens"], queryFn: () => explore.getTokens(), ...EXPLORE_QUERY });
  const data = useMemo(
    () => (query.data ? query.data.map((token) => withListData(token, listTokens)) : undefined),
    [query.data, listTokens]
  );
  return { ...query, data };
};

// pools of the KoinDX periphery only
export const useExplorePools = () => {
  const listTokens = useListTokens();
  const query = useQuery({ queryKey: ["explore", "pools"], queryFn: () => explore.getPools(), ...EXPLORE_QUERY });
  const data = useMemo(
    () =>
      query.data
        ? query.data.filter((pool) => pool.protocolId === PERIPHERY).map((pool) => withPoolTokens(pool, listTokens))
        : undefined,
    [query.data, listTokens]
  );
  return { ...query, data };
};

/**
 * One token: from the shared list, or read alone when the list leaves it out (no liquidity left)
 */
export const useExploreToken = (id) => {
  const listTokens = useListTokens();
  const list = useExploreTokens();
  const fromList = list.data ? list.data.find((token) => token.id === id) : undefined;
  const single = useQuery({
    queryKey: ["explore", "token", id],
    queryFn: () => explore.getToken(id),
    enabled: Boolean(id) && list.isFetched && !fromList,
    retry: false,
    ...EXPLORE_QUERY,
  });
  return {
    data: fromList || withListData(single.data, listTokens),
    isLoading: list.isLoading || (single.fetchStatus === "fetching" && !single.data),
    notFound: list.isFetched && !fromList && single.isError && _get(single.error, "response.status") === 404,
    isError: !fromList && (list.isError || (single.isError && _get(single.error, "response.status") !== 404)),
    refetch: () => Promise.all([list.refetch(), single.refetch()]),
  };
};

/**
 * One pool: from the shared list, or read alone (a pool without USD liquidity is not in the list)
 */
export const useExplorePool = (id) => {
  const listTokens = useListTokens();
  const list = useExplorePools();
  const fromList = list.data ? list.data.find((pool) => pool.id === id) : undefined;
  const single = useQuery({
    queryKey: ["explore", "pool", id],
    queryFn: () => explore.getPool(id),
    enabled: Boolean(id) && list.isFetched && !fromList,
    retry: false,
    ...EXPLORE_QUERY,
  });
  return {
    data: fromList || withPoolTokens(single.data, listTokens),
    isLoading: list.isLoading || (single.fetchStatus === "fetching" && !single.data),
    notFound: list.isFetched && !fromList && single.isError && _get(single.error, "response.status") === 404,
    isError: !fromList && (list.isError || (single.isError && _get(single.error, "response.status") !== 404)),
    refetch: () => Promise.all([list.refetch(), single.refetch()]),
  };
};
